import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import API from "@/lib/api";
import PublicFooter from "@/components/PublicFooter";
import { Button } from "@/components/ui/button";
import {
  Truck, Tag, ShieldCheck, MessageSquare, Beaker, Snowflake,
  CheckCircle2, ArrowRight, Mail,
} from "lucide-react";

const PILLARS = [
  {
    icon: Truck,
    title: "Timely shipping",
    body:
      "Same-day or next-day dispatch on approved orders. Patients on weight-loss therapy lose progress fast when refills slip — partners that ship within 24 hours move to the front of our rotation.",
  },
  {
    icon: Tag,
    title: "Clean, transparent pricing",
    body:
      "No surprise fees, no quiet markups. We pass cost through to patients honestly, so we partner only with pharmacies whose pricing matches their compounding spec sheet — line for line.",
  },
  {
    icon: Snowflake,
    title: "Cold-chain integrity",
    body:
      "Validated 2-8 °C packaging with temperature monitoring on every shipment. We share our shipping-stability data publicly; partners should be able to do the same.",
  },
  {
    icon: Beaker,
    title: "503A or 503B compounding",
    body:
      "USP <797> sterile compounding, current state board licensure, and a clear story on raw-material sourcing (USP-grade APIs only).",
  },
  {
    icon: ShieldCheck,
    title: "HIPAA readiness",
    body:
      "Mutual BAA on file before any patient data flows. We sign yours, you sign ours. SFTP / API integration, MFA on every account, and audit logging on dispense events.",
  },
  {
    icon: MessageSquare,
    title: "Responsive support",
    body:
      "A real human on a same-day Slack/email turnaround for clinical or shipping questions. We escalate fast when patients need attention; partners should match that pace.",
  },
];

const STATES = [
  "", "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM",
  "NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA",
  "WV","WI","WY","DC","PR",
];

const LICENSE_OPTIONS = ["503A", "503B", "Both 503A + 503B", "Retail / Other"];

export default function PharmacyAffiliatePage() {
  const [form, setForm] = useState({
    contact_name: "",
    pharmacy_name: "",
    email: "",
    phone: "",
    state: "",
    license_type: "503A",
    nabp_or_dea: "",
    capabilities: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const valid =
    form.contact_name.trim().length >= 2 &&
    form.pharmacy_name.trim().length >= 2 &&
    /\S+@\S+\.\S+/.test(form.email) &&
    form.message.trim().length >= 20;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) {
      toast.error("Please fill in your name, pharmacy, a valid email, and a brief message (20+ chars).");
      return;
    }
    setBusy(true);
    try {
      await API.post("/pharmacy-affiliate/inquiry", form);
      setSubmitted(true);
      toast.success("Inquiry received. We'll be in touch within 1-2 business days.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again or email partners@glpflo.org.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-violet-50" data-testid="pharmacy-affiliate-page">
      {/* Compact top nav-bar */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            GLP<span className="text-purple-600">Flo</span>
          </Link>
          <Link to="/" className="text-sm text-slate-500 hover:text-slate-900">← Back to home</Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-10 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium mb-5">
          <Beaker className="w-3.5 h-3.5" /> Pharmacy partner program
        </div>
        <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Dispense for{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-fuchsia-500">
            GLPFlo
          </span>
        </h1>
        <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
          We work with a small bench of compounding pharmacies that share our
          standards on quality, transparency, and patient experience. If that
          sounds like you, we&apos;d love to talk.
        </p>
        <a
          href="#inquiry-form"
          className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-purple-600 text-white font-semibold hover:bg-purple-700 shadow-md hover:shadow-lg transition-all"
          data-testid="affiliate-hero-cta"
        >
          Start the conversation <ArrowRight className="w-4 h-4" />
        </a>
      </section>

      {/* What we look for */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">
        <div className="text-center mb-10">
          <p className="text-xs uppercase tracking-widest text-purple-600 font-semibold mb-2">What we look for</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Six things that move you to the front of our rotation
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.title}
                className="group rounded-2xl bg-white border border-slate-200 p-6 hover:border-purple-300 hover:shadow-lg transition-all"
                data-testid={`pillar-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-100 to-fuchsia-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Icon className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{p.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Inquiry form */}
      <section id="inquiry-form" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="rounded-3xl bg-white border border-purple-200 shadow-xl p-6 sm:p-10">
          {submitted ? (
            <div className="text-center py-10" data-testid="affiliate-success">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Thanks — we got it.
              </h2>
              <p className="text-slate-600 max-w-md mx-auto">
                A real person will read your note and reply within
                1-2 business days. If it&apos;s urgent, email{" "}
                <a href="mailto:partners@glpflo.org" className="text-purple-600 font-medium hover:underline">
                  partners@glpflo.org
                </a>{" "}
                directly.
              </p>
              <Link
                to="/"
                className="inline-flex items-center gap-2 mt-6 text-purple-600 hover:underline"
                data-testid="affiliate-back-home"
              >
                ← Back to home
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Tell us about your pharmacy
              </h2>
              <p className="text-slate-600 mb-6 text-sm">
                We&apos;ll respond within 1-2 business days. Prefer email?{" "}
                <a href="mailto:partners@glpflo.org" className="text-purple-600 font-medium hover:underline">
                  partners@glpflo.org
                </a>
              </p>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Your name *">
                    <input
                      data-testid="affiliate-contact-name"
                      type="text" required value={form.contact_name} onChange={update("contact_name")}
                      className="form-input" />
                  </Field>
                  <Field label="Pharmacy name *">
                    <input
                      data-testid="affiliate-pharmacy-name"
                      type="text" required value={form.pharmacy_name} onChange={update("pharmacy_name")}
                      className="form-input" />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Email *">
                    <input
                      data-testid="affiliate-email"
                      type="email" required value={form.email} onChange={update("email")}
                      className="form-input" />
                  </Field>
                  <Field label="Phone">
                    <input
                      data-testid="affiliate-phone"
                      type="tel" value={form.phone} onChange={update("phone")}
                      className="form-input" />
                  </Field>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <Field label="State">
                    <select data-testid="affiliate-state" value={form.state} onChange={update("state")} className="form-input">
                      {STATES.map((s) => <option key={s} value={s}>{s || "Select…"}</option>)}
                    </select>
                  </Field>
                  <Field label="License type *">
                    <select data-testid="affiliate-license-type" value={form.license_type} onChange={update("license_type")} className="form-input">
                      {LICENSE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </Field>
                  <Field label="NABP / DEA">
                    <input
                      data-testid="affiliate-nabp"
                      type="text" placeholder="optional" value={form.nabp_or_dea} onChange={update("nabp_or_dea")}
                      className="form-input" />
                  </Field>
                </div>
                <Field label="What you currently dispense">
                  <textarea
                    data-testid="affiliate-capabilities"
                    rows={2}
                    placeholder="Semaglutide, tirzepatide, B-12, peptides, etc."
                    value={form.capabilities} onChange={update("capabilities")} className="form-input" />
                </Field>
                <Field label="Your message *">
                  <textarea
                    data-testid="affiliate-message"
                    rows={5} required minLength={20} maxLength={2000}
                    placeholder="Tell us a bit about your operation — turnaround time, cold-chain process, capacity, why you'd be a good fit."
                    value={form.message} onChange={update("message")} className="form-input" />
                  <p className="text-xs text-slate-400 mt-1">{form.message.length}/2000 · minimum 20 characters</p>
                </Field>

                <div className="pt-2 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                  <p className="text-xs text-slate-500 flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5" />
                    We never share your info with patients or third parties.
                  </p>
                  <Button
                    type="submit"
                    data-testid="affiliate-submit"
                    disabled={busy || !valid}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-7 py-2.5 rounded-full font-semibold shadow-md disabled:opacity-60"
                  >
                    {busy ? "Sending…" : "Send inquiry"}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </section>

      <style>{`
        .form-input {
          width: 100%;
          background: #fafafa;
          border: 1px solid #e2e8f0;
          border-radius: 0.5rem;
          padding: 0.65rem 0.85rem;
          font-size: 0.875rem;
          transition: border-color .15s, box-shadow .15s;
        }
        .form-input:focus {
          outline: none;
          border-color: #a78bfa;
          box-shadow: 0 0 0 3px rgba(167, 139, 250, 0.25);
          background: #ffffff;
        }
      `}</style>

      <PublicFooter />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
